import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthUser, Organization } from "@odingard/sdk-client";
import { api, getToken, setToken, setUnauthorizedHandler } from "./api";

interface AuthState {
  user: AuthUser | null;
  organizations: Organization[];
  activeOrg: Organization | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<{ verificationSent: boolean; verificationLink?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setActiveOrg: (orgId: string) => void;
}

const AuthContext = createContext<AuthState | null>(null);
const ACTIVE_ORG_KEY = "ods.active.org";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_ORG_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setOrganizations([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.me();
      setUser(res.user);
      setOrganizations(res.organizations);
    } catch {
      setUser(null);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setOrganizations([]);
    });
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.login({ email, password });
      setToken(res.token);
      await refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const res = await api.register(displayName ? { email, password, displayName } : { email, password });
      setToken(res.token);
      await refresh();
      return {
        verificationSent: res.verification?.sent ?? false,
        ...(res.verification?.link ? { verificationLink: res.verification.link } : {}),
      };
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* best effort */
    }
    setToken(null);
    setUser(null);
    setOrganizations([]);
  }, []);

  const setActiveOrg = useCallback((orgId: string) => {
    setActiveOrgId(orgId);
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    } catch {
      /* ignore */
    }
  }, []);

  const activeOrg = useMemo(() => {
    if (organizations.length === 0) return null;
    return organizations.find((o) => o.id === activeOrgId) ?? organizations[0] ?? null;
  }, [organizations, activeOrgId]);

  const value = useMemo<AuthState>(
    () => ({ user, organizations, activeOrg, loading, login, register, logout, refresh, setActiveOrg }),
    [user, organizations, activeOrg, loading, login, register, logout, refresh, setActiveOrg],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
